import React, { useState } from 'react';
import { X, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
// import { toast } from 'react-toastify';

// Custom Toast Component
const CustomToast = ({ message, type }) => (
  <div className="flex items-start gap-3">
    {type === 'success' ? (
      <CheckCircle className="w-6 h-6 text-[#6bee2a] flex-shrink-0 mt-0.5" />
    ) : (
      <XCircle className="w-6 h-6 text-[#6bee2a] flex-shrink-0 mt-0.5" />
    )}
    <div className="flex-1">
      <div className="text-white font-semibold text-lg mb-1">
        {type === 'success' ? 'Success!' : 'Error!'}
      </div>
      <div className="text-gray-300 text-sm leading-relaxed">{message}</div>
    </div>
  </div>
);

const ApplicationModal = ({ isOpen, onClose, jobTitle }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobileNumber: '',
    role: '',
    resume: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      resume: e.target.files[0],
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('mobileNumber', formData.mobileNumber);
      formDataToSend.append('role', jobTitle);

      // Rename resume file to include job position
      const fileExtension = formData.resume.name.split('.').pop();
      const newFileName = `${jobTitle.replace(/\s+/g, '_')}_${formData.name.replace(/\s+/g, '_')}.${fileExtension}`;
      const renamedFile = new File([formData.resume], newFileName, {
        type: formData.resume.type,
      });

      formDataToSend.append('resume', renamedFile);

     const response = await fetch(
       `${process.env.REACT_APP_BASE_URL}applications/apply`,
       {
         method: 'POST',
         body: formDataToSend,
       }
     );


      const data = await response.json();

      if (response.ok) {
        toast(
          <CustomToast
            message={data.message || 'Application submitted successfully!'}
            type="success"
          />,
          {
            position: 'top-right',
            autoClose: 7000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            icon: false,
            style: {
              background: '#1a1a1a',
              border: 'px solid #6bee2a',
              borderRadius: '12px',
              padding: '16px',
              minWidth: '400px',
            },
            closeButton: ({ closeToast }) => (
              <button
                onClick={closeToast}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            ),
          }
        );

        onClose();
        setFormData({ name: '', email: '', mobileNumber: '', resume: null });
      } else {
        toast(
          <CustomToast
            message={
              data.message || 'Failed to submit application. Please try again.'
            }
            type="error"
          />,
          {
            position: 'top-right',
            autoClose: 7000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            icon: false,
            style: {
              background: '#1a1a1a',
              border: 'px solid #6bee2a',
              borderRadius: '12px',
              padding: '16px',
              minWidth: '400px',
            },
            closeButton: ({ closeToast }) => (
              <button
                onClick={closeToast}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            ),
          }
        );
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast(
        <CustomToast
          message="Something went wrong. Please try again later."
          type="error"
        />,
        {
          position: 'top-right',
          autoClose: 7000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          icon: false,
          style: {
            background: '#1a1a1a',
            border: '2px solid #EE2B2A',
            borderRadius: '12px',
            padding: '16px',
            minWidth: '400px',
          },
          closeButton: ({ closeToast }) => (
            <button
              onClick={closeToast}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          ),
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 mt-16">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black bg-opacity-75"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-[#1a1a1a] border border-[#262626] rounded-lg w-full max-w-md p-6 z-10 my-6 max-h-[70vh] overflow-y-auto scrollbar">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[#98989A] hover:text-white transition-colors"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <h2 className="text-xl font-jakarta text-white mb-1">
          Apply for {jobTitle}
        </h2>
        <p className="text-[#98989A] text-sm mb-4">
          Fill out the form below to submit your application
        </p>

        {/* Form */}
        <div className="space-y-3">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm text-[#98989A] mb-1">
              Full Name <span className="text-[#EE2B2A]">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full bg-[#262626] border border-[#404040] rounded-md px-4 py-2 text-white text-sm focus:outline-none focus:border-[#EE2B2A] transition-colors"
              placeholder="Enter your full name"
            />
          </div>

          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm text-[#98989A] mb-1"
            >
              Email Address <span className="text-[#EE2B2A]">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full bg-[#262626] border border-[#404040] rounded-md px-4 py-2 text-white text-sm focus:outline-none focus:border-[#EE2B2A] transition-colors"
              placeholder="your.email@example.com"
            />
          </div>

          {/* Mobile */}
          <div>
            <label
              htmlFor="mobileNumber"
              className="block text-sm text-[#98989A] mb-1"
            >
              Mobile Number<span className="text-[#EE2B2A]">*</span>
            </label>
            <input
              type="tel"
              id="mobileNumber"
              name="mobileNumber"
              value={formData.mobileNumber}
              onChange={handleChange}
              className="w-full bg-[#262626] border border-[#404040] rounded-md px-4 py-2 text-white text-sm focus:outline-none focus:border-[#EE2B2A] transition-colors"
              placeholder="Enter your mobile number"
            />
          </div>

          {/* Resume */}
          <div>
            <label
              htmlFor="resume"
              className="block text-sm text-[#98989A] mb-1"
            >
              Resume (PDF) <span className="text-[#EE2B2A]">*</span>
            </label>
            <input
              type="file"
              id="resume"
              name="resume"
              onChange={handleFileChange}
              accept=".pdf"
              className="w-full bg-[#262626] border border-[#404040] rounded-md px-3 py-2 text-white text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-[#404040] file:text-white file:cursor-pointer hover:file:bg-[#4a4a4a] transition-colors"
            />
            {formData.resume && (
              <p className="text-xs text-[#98989A] mt-1">
                Selected: {formData.resume.name}
              </p>
            )}
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              !formData.name ||
              !formData.email ||
              !formData.mobileNumber ||
              !formData.resume
            }
            className="w-full bg-[#EE2B2A] text-white py-2.5 rounded-md text-sm font-jakarta hover:bg-[#d02625] disabled:bg-[#404040] disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </button>
        </div>
      </div>
    </div>
  );

};

export default ApplicationModal;
